import { useEffect, useMemo } from 'react';
import { useGetVisibilityScoreQuery } from 'store/api/apiSlice';
import { useAppSelector } from 'store/hooks';
import { useApiError } from './useApiError';
import { shouldFetchReports } from './fetchEligibility';
import { usePollingOnError } from 'shared/hooks/usePollingOnError';
import {
  VisibilityScoreRequest,
  VisibilityScoreResponse,
} from 'store/api/apiTypes';

/**
 * useVisibilityScore
 * Fetches LLM visibility score data for the currently selected project (company + products).
 */
export const useVisibilityScore = () => {
  const selectedProject = useAppSelector(
    state => state.project.selectedProject
  );
  const activeReport = useAppSelector(state => state.history.activeReport);

  const queryParams = useMemo<VisibilityScoreRequest | null>(() => {
    if (!selectedProject?.domain) return null;
    if (!shouldFetchReports(selectedProject)) return null;
    return { domain: selectedProject.domain, reportId: activeReport?.id };
  }, [selectedProject, activeReport]);

  const {
    data: visibilityScore,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetVisibilityScoreQuery(queryParams as VisibilityScoreRequest, {
    skip: !queryParams,
    refetchOnMountOrArgChange: true,
  });

  const { showErrorModal, handleApiError, handleRetry, hideErrorModal } =
    useApiError(() => refetch());

  useEffect(() => {
    if (error) {
      handleApiError(error);
    }
  }, [error, handleApiError]);

  usePollingOnError(queryParams, error, refetch, visibilityScore);

  const companyEntity = visibilityScore?.company?.entity;

  return {
    visibilityScore: visibilityScore as VisibilityScoreResponse | undefined,
    isLoading,
    isFetching,
    error,
    refetchVisibilityScore: refetch,
    showVisibilityScoreErrorModal: showErrorModal,
    handleVisibilityScoreRetry: handleRetry,
    hideVisibilityScoreErrorModal: hideErrorModal,
    hasVisibilityScore: !!visibilityScore,
    companyEntity,
    companyName: companyEntity?.name,
    companyOverallScore: companyEntity?.overall_score,
    productBlocks: visibilityScore?.products || {},
  };
};
